import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Container, Header, Segment } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { userOnStartExam, userOnRetryExam, userClearOnGoingExam, userGetOnGoingExam, userExamInfoChanged, userGetSelectedCourseStruct } from '../../../actions';
import { Consts } from '../../../res';
import McqsResult from './McqsResult';
import EssayResult from './EssayResult';
import MockResultTemplate from './MockResultTemplates';
import LastMockResult from './LastMockResult';
import Separator from '../../Operantinals/Separator';
import { findNextFreqExam, getExamByExamId, resolveExam } from '../../../helpers/examHelpers';
import history from '../../../history';
import PATHS from '../../../Routers/PATHS';
import { getBookExamReturnPath } from '../../../features/interactive-book/book-exam-return';


const Results = (props) => {
    const { onGoingExam, t, userClearOnGoingExam, userOnStartExam, userExamInfoChanged, userGetOnGoingExam,
        selectedCourse, userGetSelectedCourseStruct, userOnRetryExam, summary, nextSibsFinished, BankResults } = props;
    const location = useLocation();
    const bookReturnPath = getBookExamReturnPath(location, onGoingExam, selectedCourse?.id);

    const to = () => {
        if (bookReturnPath) return bookReturnPath;
        switch (onGoingExam.examType) {
            case Consts.EXAM_TYPES.MCQS:
                return PATHS.USER_BANK_MCQS.URL;
            case Consts.EXAM_TYPES.ESSAY:
                return PATHS.USER_BANK_ESSAY.URL;
            case Consts.EXAM_TYPES.TBS:
                return PATHS.USER_BANK_TBS.URL;
            case Consts.EXAM_TYPES.FILE:
                return PATHS.USER_BANK_FILE.URL;
            case Consts.EXAM_TYPES.TEST_MCQS:
            case Consts.EXAM_TYPES.TEST_ESSAY:
            case Consts.EXAM_TYPES.TEST_TBS:
            case Consts.EXAM_TYPES.TEST_FILE:
                return `${PATHS.USER_COURSE_DETAILS.URL}?courseId=${selectedCourse.id}&topicId=${onGoingExam?.topicId}`;
            case Consts.EXAM_TYPES.FREQ:
                return `${PATHS.USER_COURSE_DETAILS.URL}?courseId=${selectedCourse.id}`;
            default:
                return `${PATHS.USER_COURSE_DETAILS.URL}?courseId=${selectedCourse.id}`;
        }
    };

    const onMoveToNextPart = async () => {
        userExamInfoChanged({ props: 'questions', value: [] });
        let nextExam;
        if (onGoingExam.examType === Consts.EXAM_TYPES.FREQ) {
            const nextFreq = findNextFreqExam(onGoingExam.parentId, onGoingExam.orderNum);
            nextExam = resolveExam(onGoingExam.examType, null, nextFreq.id);
        }
        if (nextExam) {
            await userOnStartExam(nextExam, nextExam?.questionsCount || 1);
            if (summary.show) summary.setShowSummary(false);
            userExamInfoChanged({ props: 'isFinished', value: false });
        }
        else {
            userExamInfoChanged({ props: 'isFinished', value: true });
            history.push(to());
        }
    };

    const renderResultSwitch = () => {
        if (onGoingExam.isViewOnly) return <Header as='h1' textAlign='center'>{t('YourViewHasFinished')}</Header>;
        switch (onGoingExam.examType) {
            case Consts.EXAM_TYPES.MCQS:
            case Consts.EXAM_TYPES.TEST_MCQS:
                return <McqsResult onGoingExam={onGoingExam} onGetOngoingExam={userGetOnGoingExam} onRetryExam={userOnRetryExam} userExamInfoChanged={userExamInfoChanged} />;
            case Consts.EXAM_TYPES.ESSAY:
            case Consts.EXAM_TYPES.TEST_ESSAY:
                return <EssayResult />;
            case Consts.EXAM_TYPES.FREQ: {
                if (!onGoingExam.parentId) return <LastMockResult onGoingExam={onGoingExam} />;
                if (nextSibsFinished || getExamByExamId(onGoingExam?.parentId)?.childs?.length === onGoingExam.orderNum)
                    return <LastMockResult onGoingExam={onGoingExam} />;
                return <MockResultTemplate onGoingExam={onGoingExam} onMoveToEssay={onMoveToNextPart} />;
            }
            default: return <Header as='h1' textAlign='center'>{t('YourExamHasFinished')}</Header>;
        }
    };

    /*
     * The redesigned report for a finished MCQ-bank attempt or MCQ knowledge
     * test, handed in by the new bank page (src/modules/student/pages/QuestionBank.jsx),
     * which serves both routes. It carries its own retry and back actions. A
     * review (`isViewOnly`) and every other exam type keep the screens below.
     */
    if (BankResults && !onGoingExam.isViewOnly
        && (onGoingExam.examType === Consts.EXAM_TYPES.MCQS || onGoingExam.examType === Consts.EXAM_TYPES.TEST_MCQS))
        return <BankResults onGoingExam={onGoingExam} />;

    return (
        <Segment basic>
            <Container style={{ width: '90vw' }}>
                {renderResultSwitch()}
                <Separator />
                {(onGoingExam.isViewOnly || onGoingExam.examType !== Consts.EXAM_TYPES.FREQ) && <Segment basic textAlign='center'>
                    <Button primary labelPosition='left' icon='arrow left' content={t('Back')} as={Link} to={to} onClick={async () => {
                        // The book reader reloads its results on return. Other
                        // destinations still refresh their course structure.
                        if (!bookReturnPath && (onGoingExam.examType === Consts.EXAM_TYPES.TEST_MCQS || onGoingExam.examType === Consts.EXAM_TYPES.TEST_ESSAY || onGoingExam.examType === Consts.EXAM_TYPES.TEST_TBS || onGoingExam.examType === Consts.EXAM_TYPES.TEST_FILE || onGoingExam.examType === Consts.EXAM_TYPES.FREQ))
                            await userGetSelectedCourseStruct(selectedCourse.id);
                        userClearOnGoingExam();
                    }} />
                </Segment>}
            </Container>
        </Segment>

    );
};

const mapStateToProps = ({ userCourses }) => {
    const { selectedCourse } = userCourses;
    return { selectedCourse };
};

export default withTranslation()(connect(mapStateToProps, { userOnStartExam, userOnRetryExam, userClearOnGoingExam, userGetOnGoingExam, userExamInfoChanged, userGetSelectedCourseStruct })(Results));
